import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Globe, Plus, PencilSimple, Trash, MagnifyingGlass, X, CheckCircle, Warning,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

const emptyForm = { name: "", domain: "", ga4_measurement_id: "" };

export default function Websites() {
  const { currentOrg } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/websites");
      setRows(data || []);
    } catch {
      toast.error("Could not load websites");
      setRows([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter(x =>
      (x.name || "").toLowerCase().includes(q) ||
      (x.domain || "").toLowerCase().includes(q));
  }, [rows, query]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (w) => {
    setEditing(w.website_id);
    setForm({ name: w.name || "", domain: w.domain || "",
              ga4_measurement_id: w.ga4_measurement_id || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    const payload = {
      name,
      domain: form.domain.trim(),
      ga4_measurement_id: form.ga4_measurement_id.trim(),
    };
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/websites/${editing}`, payload);
        setRows(all => all.map(x => x.website_id === editing ? data : x));
        toast.success("Website updated");
      } else {
        const { data } = await api.post("/websites", payload);
        setRows(all => [data, ...all]);
        toast.success("Website created");
      }
      setDialogOpen(false); setEditing(null); setForm(emptyForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/websites/${confirmDelete.website_id}`);
      setRows(all => all.filter(x => x.website_id !== confirmDelete.website_id));
      toast.success(`Removed "${confirmDelete.name}"`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="websites-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Globe weight="duotone" className="h-3 w-3" /> Marketing · Websites
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Websites</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Register the websites you own and paste each one's GA4 Measurement ID so we can auto-inject
            the tracking script into any Landing Page linked to that website. Metrics posted to
            <code className="mx-1 px-1 py-0.5 rounded bg-graphite-950 text-ink-primary/80 text-[11px]">
              /api/inbound/website-metrics/batch
            </code>
            will roll up on the Website Analytics dashboard.
          </p>
        </div>
        <button data-testid="websites-new-btn" onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200">
          <Plus weight="bold" className="h-4 w-4" /> New website
        </button>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <Input data-testid="websites-search" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search by name or domain"
          className="pl-9 bg-graphite-950 border-white/10" />
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary" data-testid="websites-loading">Loading websites…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-12 text-center"
             data-testid="websites-empty">
          <Globe weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/60" />
          <div className="mt-4 font-display text-2xl tracking-tight">No websites yet</div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            Add a website to enable GA4 tracking on your landing pages and unlock the Website
            Analytics dashboard.
          </p>
          <button onClick={openCreate}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200">
            <Plus weight="bold" className="h-4 w-4" /> New website
          </button>
        </div>
      ) : (
        <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden"
             data-testid="websites-list">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-[10px] uppercase tracking-widest text-ink-secondary">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Domain</th>
                <th className="text-left px-4 py-3 font-medium">Measurement ID</th>
                <th className="text-left px-4 py-3 font-medium">Tracking</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(w => (
                <tr key={w.website_id} data-testid={`websites-row-${w.website_id}`}
                    className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-ink-primary">{w.name}</td>
                  <td className="px-4 py-3 text-ink-secondary font-mono text-xs">{w.domain || "—"}</td>
                  <td className="px-4 py-3 text-ink-secondary font-mono text-xs">
                    {w.ga4_measurement_id || <span className="opacity-60">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {w.tracking_status === "configured" ? (
                      <span data-testid={`websites-tracking-status-${w.website_id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-2 py-0.5 text-[10px] uppercase tracking-widest">
                        <CheckCircle weight="fill" className="h-3 w-3" /> Configured
                      </span>
                    ) : (
                      <span data-testid={`websites-tracking-status-${w.website_id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-graphite-950 text-ink-secondary px-2 py-0.5 text-[10px] uppercase tracking-widest">
                        <Warning weight="regular" className="h-3 w-3" /> Not configured
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button data-testid={`websites-edit-${w.website_id}`} onClick={() => openEdit(w)}
                        className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5">
                        <PencilSimple weight="bold" className="h-4 w-4" />
                      </button>
                      <button data-testid={`websites-delete-${w.website_id}`} onClick={() => setConfirmDelete(w)}
                        className="p-2 rounded-md text-red-300/80 hover:text-red-200 hover:bg-red-500/10">
                        <Trash weight="bold" className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); } } }}>
        <DialogContent data-testid="websites-dialog" className="max-w-lg bg-graphite-900 border-white/10 text-ink-primary">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">{editing ? "Edit website" : "New website"}</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Only the name is required. Add a GA4 Measurement ID to enable auto-injection on
              linked Landing Pages.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
              <Input data-testid="websites-form-name" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                maxLength={200} placeholder="e.g. apexgrey.com"
                className="bg-graphite-950 border-white/10" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Domain (optional)</label>
              <Input data-testid="websites-form-domain" value={form.domain}
                onChange={e => setForm({ ...form, domain: e.target.value })}
                placeholder="apexgrey.com" maxLength={200}
                className="bg-graphite-950 border-white/10 font-mono text-xs" />
              <p className="text-[11px] text-ink-secondary/70">Protocol and trailing slash are stripped automatically.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">GA4 Measurement ID (optional)</label>
              <Input data-testid="websites-form-mid" value={form.ga4_measurement_id}
                onChange={e => setForm({ ...form, ga4_measurement_id: e.target.value.toUpperCase() })}
                placeholder="G-XXXXXXXXXX" maxLength={20}
                className="bg-graphite-950 border-white/10 font-mono text-xs" />
              <p className="text-[11px] text-ink-secondary/70">Must match <code>G-XXXXXXXXXX</code>. Leave empty for a placeholder website.</p>
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <button data-testid="websites-form-cancel" onClick={() => { if (!saving) setDialogOpen(false); }} disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">Cancel</button>
            <button data-testid="websites-form-save" onClick={save} disabled={saving || !form.name.trim()}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Create website"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={v => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="websites-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove website?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              {confirmDelete?.name} will be soft-deleted. Any Landing Page linked to it will
              lose its GA4 tracking automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="websites-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="websites-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
